/**
 * Pure mapping helpers that translate merged-schema `race_events` rows into the
 * client-facing `Race` shape the V2 components consume.
 *
 * WHY this file exists (merge context):
 * The public-live page was originally written against as-checkin's old `races`
 * table. The merged Race Command schema renamed that table to `race_events` and
 * changed two things that would otherwise leak straight into render:
 *   1. `races.date`   became `race_events.date_start`  (a column RENAME).
 *   2. `races.status` used the vocabulary `draft|active|completed`, while
 *      `race_events.status` uses `planning|active|completed|cancelled`
 *      (the rc CHECK constraint forbids the literal `draft`).
 *
 * These helpers are deliberately kept PURE (data in, data out — no Supabase
 * client, no `import.meta.env`) so they can be unit-tested in isolation. That
 * matters here specifically: importing `queries.ts` pulls in `supabase.ts`,
 * which throws at module load when the VITE_SUPABASE_* env vars are absent (as
 * they are under Vitest). Living in their own module lets the tests import the
 * mappers without booting a Supabase client.
 */

import type { ElevationProfile } from '@as-checkin/shared';
import type { Race } from './types';

/**
 * Translate a merged-schema `race_events.status` value into the vocabulary the
 * client (and the PENDING badge in RaceHeaderV2) expects.
 *
 * DB CHECK on `race_events.status`:  planning | active | completed | cancelled
 * Client/badge vocabulary:           draft    | active | completed | cancelled
 *
 * Only `planning` needs translating — it maps to `draft`, the pre-start state
 * RaceHeaderV2 renders as "PENDING". `active`, `completed`, and `cancelled` are
 * identical in both vocabularies and pass through unchanged. Any unexpected
 * value (null / undefined / a future status literal) defaults to `draft` — the
 * safe pre-start state — so a raw DB literal can never reach the badge renderer.
 *
 * @param dbStatus - the raw `race_events.status` value (or null/undefined)
 * @returns the client-facing status literal
 */
export function mapRaceStatus(
  dbStatus: string | null | undefined
): 'draft' | 'active' | 'completed' | 'cancelled' {
  switch (dbStatus) {
    case 'planning':
      // VALUE translation (dictionary §1): rc `planning` === client `draft`.
      return 'draft';
    case 'active':
    case 'completed':
    case 'cancelled':
      // Literal in both vocabularies — pass through untouched.
      return dbStatus;
    default:
      // Null / undefined / unknown → treat as pre-start so nothing raw renders.
      return 'draft';
  }
}

/**
 * Normalize a raw `race_events` row into the client-facing `Race` object.
 *
 * Runs the two remaps that must never leak to render:
 *   - `date_start` → `date`            (column RENAME)
 *   - `status`     → mapRaceStatus(...) (planning → draft)
 *
 * WHY this is shared: it is used by BOTH the initial fetch (queries.ts) and the
 * Supabase Realtime handler (useRaceData.ts). Realtime UPDATE payloads deliver
 * the raw DB row — wire name `date_start`, value `planning` — so the exact same
 * normalization has to run there too. Without it, a live status/date change
 * would repopulate the Race object with un-mapped values (blank date, a
 * "PLANNING" badge). Object fields are built explicitly (rather than spread) so
 * `date_start` can never survive onto the client object.
 *
 * @param row - a raw `race_events` row from a SELECT or a Realtime payload
 * @returns the normalized `Race`
 */
export function normalizeRaceRow(row: Record<string, unknown>): Race {
  return {
    id: row.id as string,
    name: row.name as string,
    slug: row.slug as string,
    // RENAME: race_events.date_start is the client's `date` field.
    date: row.date_start as string,
    start_time: (row.start_time as string | null) ?? null,
    distance: (row.distance as string | null) ?? null,
    location: (row.location as string | null) ?? null,
    // VALUE translation: planning → draft so RaceHeaderV2 shows PENDING.
    status: mapRaceStatus(row.status as string | null | undefined),
    test_mode: (row.test_mode as boolean | undefined) ?? false,
    // Optional fields — `undefined`/`null` when a lightweight SELECT omits them.
    time_zone: (row.time_zone as string | undefined) ?? undefined,
    elevation_profile: (row.elevation_profile as ElevationProfile | null | undefined) ?? null,
    youtube_livestream_url:
      (row.youtube_livestream_url as string | null | undefined) ?? null,
    spot_tracker_url:
      (row.spot_tracker_url as string | null | undefined) ?? null,
  };
}
